/**
 * Other dated documents (FR-PAR-34): the insurance certificate a collector must hold (loi 28-00
 * art. 36), ISO certificates, laboratory accreditations, and the ADR evidence of a transporter
 * (FR-FIA-05, loi 30-05): driver training, safety adviser, vehicle certificate and the fiche de
 * sécurité. Each belongs to the site or to a prestataire, carries a reference and a validity, and
 * raises the same expiry alerts as the CAP and the authorizations (FR-TRV-02).
 */
import { z } from 'zod';
export const OTHER_DOCUMENT_KINDS = [
    'ASSURANCE',
    'CERTIFICAT_ISO',
    'ACCREDITATION_LABORATOIRE',
    'ADR_FORMATION_CONDUCTEUR',
    'ADR_CONSEILLER_SECURITE',
    'ADR_CERTIFICAT_VEHICULE',
    'ADR_FICHE_SECURITE',
    'AUTRE',
];
/** The ADR evidence of a transporter (FR-FIA-05), watched as their own alert kind. */
export const ADR_EVIDENCE_KINDS = [
    'ADR_FORMATION_CONDUCTEUR',
    'ADR_CONSEILLER_SECURITE',
    'ADR_CERTIFICAT_VEHICULE',
    'ADR_FICHE_SECURITE',
];
/** Labels for documents and e-mails produced by the server. */
export const OTHER_DOCUMENT_KIND_LABELS = {
    ASSURANCE: {
        fr: "Attestation d'assurance",
        ar: 'شهادة التأمين',
        en: 'Insurance certificate',
    },
    CERTIFICAT_ISO: { fr: 'Certificat ISO', ar: 'شهادة الإيزو', en: 'ISO certificate' },
    ACCREDITATION_LABORATOIRE: {
        fr: 'Accréditation de laboratoire',
        ar: 'اعتماد المختبر',
        en: 'Laboratory accreditation',
    },
    ADR_FORMATION_CONDUCTEUR: {
        fr: 'Certificat de formation ADR du conducteur',
        ar: 'شهادة تكوين السائق ADR',
        en: 'ADR driver training certificate',
    },
    ADR_CONSEILLER_SECURITE: {
        fr: 'Certificat du conseiller à la sécurité',
        ar: 'شهادة مستشار السلامة',
        en: 'Safety adviser certificate',
    },
    ADR_CERTIFICAT_VEHICULE: {
        fr: "Certificat d'agrément ADR du véhicule",
        ar: 'شهادة اعتماد المركبة ADR',
        en: 'ADR vehicle approval certificate',
    },
    ADR_FICHE_SECURITE: { fr: 'Fiche de sécurité', ar: 'بطاقة السلامة', en: 'Safety sheet' },
    AUTRE: { fr: 'Autre document', ar: 'وثيقة أخرى', en: 'Other document' },
};
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const uuid = z.string().uuid();
const fields = {
    kind: z.enum(OTHER_DOCUMENT_KINDS),
    reference: z.string().trim().min(1).max(120),
    validFrom: isoDate,
    validTo: isoDate,
    notes: z.string().trim().max(1000).optional(),
};
const validityOrder = (d) => !d.validFrom || !d.validTo || d.validTo >= d.validFrom;
const validityMessage = { message: 'The end of validity is before its start', path: ['validTo'] };
/** A document of the site, or of a prestataire; exactly one of the two. */
export const otherDocumentCreateSchema = z
    .object({
    siteId: uuid.nullable().optional(),
    prestataireId: uuid.nullable().optional(),
    /** The earlier document of the same holder this one renews; it then stops alerting. */
    replacesId: uuid.nullable().optional(),
    ...fields,
})
    .refine((d) => Boolean(d.siteId) !== Boolean(d.prestataireId), {
    message: 'A document belongs to the site or to a prestataire',
    path: ['prestataireId'],
})
    .refine(validityOrder, validityMessage);
export const otherDocumentUpdateSchema = z
    .object(fields)
    .partial()
    .refine(validityOrder, validityMessage);
export const otherDocumentListQuerySchema = z
    .object({
    siteId: uuid.optional(),
    prestataireId: uuid.optional(),
})
    .refine((q) => Boolean(q.siteId) !== Boolean(q.prestataireId), {
    message: 'Give the site or the prestataire',
});
/**
 * The ADR evidence a transporter lacks for a chargement (FR-FIA-05, BR-11): a driver training
 * certificate (ADR 8.2.1.1) and a safety adviser (1.8.3), and the vehicle approval certificate of
 * a tank vehicle (9.1.3.1), each valid on the date of the pickup. Under the 1.1.3.6 exemption
 * none is required: Parts 8 and 9 do not apply (1.1.3.6.2), nor the adviser for such quantities
 * (1.8.3.2 b). The fiche de sécurité of FR-FIA-05 is kept with the other documents but is not
 * an ADR requirement.
 */
export function adrEvidenceGaps(documents, context) {
    if (context.regime1136Claimed)
        return [];
    const required = ['ADR_FORMATION_CONDUCTEUR', 'ADR_CONSEILLER_SECURITE'];
    if (context.tankCarriage)
        required.push('ADR_CERTIFICAT_VEHICULE');
    const gaps = [];
    for (const kind of required) {
        const recorded = documents.filter((d) => d.kind === kind && d.active);
        if (recorded.some((d) => d.validFrom <= context.date && context.date <= d.validTo))
            continue;
        // a renewal recorded ahead of its start says more than the certificate it replaces
        const next = recorded
            .filter((d) => d.validFrom > context.date)
            .map((d) => d.validFrom)
            .sort()[0];
        const last = recorded
            .map((d) => d.validTo)
            .sort()
            .at(-1);
        gaps.push(next
            ? { kind, state: 'NOT_YET_VALID', date: next }
            : last
                ? { kind, state: 'EXPIRED', date: last }
                : { kind, state: 'MISSING' });
    }
    return gaps;
}
//# sourceMappingURL=other-documents.js.map